import { useEffect, useRef } from "react";
import { Bold, Heading2, Pilcrow, ImagePlus, WrapText, Italic, Underline, Link as LinkIcon, Minus, Plus } from "lucide-react";
import { useFileUpload } from "@/lib/uploadProgress";
import { toast } from "sonner";
import { extractErr } from "@/lib/api";
import { toEditorHtml } from "@/lib/productDescription";

export default function ProductDescriptionEditor({ value, onChange, testId = "pf-description", rich = false }) {
  const ref = useRef(null);
  const fileRef = useRef(null);
  const uploadFile = useFileUpload();
  const html = toEditorHtml(value);

  useEffect(() => {
    const el = ref.current;
    if (!el || document.activeElement === el) return;
    if (el.innerHTML !== html) el.innerHTML = html;
  }, [html]);

  const emit = () => {
    const el = ref.current;
    if (!el) return;
    onChange(el.innerHTML);
  };

  const cmd = (command, arg) => {
    ref.current?.focus();
    document.execCommand(command, false, arg);
    emit();
  };

  const insertImage = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const data = await uploadFile("/merchant/media/upload", fd);
      if (data?.url) cmd("insertImage", data.url);
      toast.success("Image added to description");
    } catch (e) {
      toast.error(extractErr(e));
    }
  };

  return (
    <div className="mt-1 rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid={`${testId}-editor`}>
      <div className="flex flex-wrap items-center gap-1 border-b border-slate-100 bg-slate-50 px-2 py-1.5">
        <button type="button" className="px-2 h-8 rounded text-xs font-semibold hover:bg-white border border-transparent hover:border-slate-200" onClick={() => cmd("bold")} data-testid={`${testId}-bold`}>
          <Bold className="w-3.5 h-3.5 inline mr-1" /> Bold
        </button>
        {rich ? (
          <>
            <button type="button" className="px-2 h-8 rounded text-xs font-semibold hover:bg-white border border-transparent hover:border-slate-200" onClick={() => cmd("italic")} data-testid={`${testId}-italic`}>
              <Italic className="w-3.5 h-3.5 inline mr-1" /> Italic
            </button>
            <button type="button" className="px-2 h-8 rounded text-xs font-semibold hover:bg-white border border-transparent hover:border-slate-200" onClick={() => cmd("underline")} data-testid={`${testId}-underline`}>
              <Underline className="w-3.5 h-3.5 inline mr-1" /> Underline
            </button>
            <button type="button" className="px-2 h-8 rounded text-xs font-semibold hover:bg-white border border-transparent hover:border-slate-200" onClick={() => {
              const href = window.prompt("Link URL");
              if (href) cmd("createLink", href);
            }} data-testid={`${testId}-link`}>
              <LinkIcon className="w-3.5 h-3.5 inline mr-1" /> Link
            </button>
            <button type="button" className="px-2 h-8 rounded text-xs font-semibold hover:bg-white border border-transparent hover:border-slate-200" onClick={() => cmd("fontSize", "2")} data-testid={`${testId}-size-sm`}>
              <Minus className="w-3.5 h-3.5 inline mr-1" /> Smaller
            </button>
            <button type="button" className="px-2 h-8 rounded text-xs font-semibold hover:bg-white border border-transparent hover:border-slate-200" onClick={() => cmd("fontSize", "5")} data-testid={`${testId}-size-lg`}>
              <Plus className="w-3.5 h-3.5 inline mr-1" /> Larger
            </button>
            <select
              className="h-8 rounded border border-transparent hover:border-slate-200 bg-transparent text-xs font-semibold px-1"
              defaultValue="sans-serif"
              data-testid={`${testId}-font`}
              onChange={(e) => cmd("fontName", e.target.value)}
            >
              <option value="sans-serif">Sans</option>
              <option value="serif">Serif</option>
              <option value="Georgia, serif">Georgia</option>
              <option value="Arial, sans-serif">Arial</option>
            </select>
          </>
        ) : null}
        <button type="button" className="px-2 h-8 rounded text-xs font-semibold hover:bg-white border border-transparent hover:border-slate-200" onClick={() => cmd("formatBlock", "H2")} data-testid={`${testId}-h2`}>
          <Heading2 className="w-3.5 h-3.5 inline mr-1" /> Heading
        </button>
        <button type="button" className="px-2 h-8 rounded text-xs font-semibold hover:bg-white border border-transparent hover:border-slate-200" onClick={() => cmd("formatBlock", "P")} data-testid={`${testId}-p`}>
          <Pilcrow className="w-3.5 h-3.5 inline mr-1" /> Paragraph
        </button>
        <button type="button" className="px-2 h-8 rounded text-xs font-semibold hover:bg-white border border-transparent hover:border-slate-200" onClick={() => cmd("insertParagraph")} data-testid={`${testId}-break`}>
          <WrapText className="w-3.5 h-3.5 inline mr-1" /> New line
        </button>
        <button type="button" className="px-2 h-8 rounded text-xs font-semibold hover:bg-white border border-transparent hover:border-slate-200" onClick={() => fileRef.current?.click()} data-testid={`${testId}-image`}>
          <ImagePlus className="w-3.5 h-3.5 inline mr-1" /> Image
        </button>
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => { insertImage(e.target.files?.[0]); e.target.value = ""; }} />
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        data-testid={testId}
        className="product-desc-editor min-h-[9rem] px-3 py-2 text-sm text-slate-800 outline-none"
        onInput={emit}
        onBlur={emit}
      />
    </div>
  );
}
